/**
 * CollapsibleSection — GlassCard-based collapsible section with framer-motion animation
 * Props: title, summary, children, defaultOpen, dark, eyebrow, icon,
 *        borderColor, info, isOpen, onToggle (controlled mode)
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GlassCard from './GlassCard';
import { colors } from '../../styles/colors';

function CollapsibleSection({
  title,
  summary,
  children,
  defaultOpen = false,
  dark = false,
  eyebrow,
  icon,
  borderColor,
  info,
  isOpen: controlledOpen,
  onToggle,
  sx = {},
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [showInfo, setShowInfo] = useState(false);

  // Controlled vs uncontrolled
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  const textColor = dark ? colors.ivory : colors.charcoal;
  const textMuted = dark ? 'rgba(250,247,242,0.5)' : colors.textMed;

  return (
    <GlassCard
      dark={dark}
      hover
      glow={false}
      delay={0}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={handleToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
      onMouseEnter={() => setShowInfo(true)}
      onMouseLeave={() => setShowInfo(false)}
      sx={{
        mb: 3,
        cursor: 'pointer',
        overflow: 'hidden',
        '&:focus-visible': {
          outline: `2px solid ${colors.primary}`,
          outlineOffset: '2px',
        },
        ...(borderColor ? { borderLeft: `3px solid ${borderColor}` } : {}),
        ...sx,
      }}
    >
      <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Title row: icon + title | chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            {icon && (
              <Box sx={{ color: dark ? colors.primaryBright : colors.primary, display: 'flex', flexShrink: 0 }}>
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {eyebrow && (
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    color: textMuted,
                    fontWeight: 600,
                    fontFamily: '"DM Sans", sans-serif',
                    mb: 0.3,
                  }}
                >
                  {eyebrow}
                </Typography>
              )}
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 500,
                  fontSize: '1.15rem',
                  color: textColor,
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ExpandMoreIcon sx={{ fontSize: 22, color: textMuted }} />
            </motion.div>
          </Box>
        </Box>

        {/* Summary — below title, visible only when closed */}
        {!open && summary && (
          <Typography
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.78rem',
              color: textMuted,
              lineHeight: 1.5,
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {summary}
          </Typography>
        )}

        {/* Collapsible content */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <Box
                sx={{ mt: 2.5 }}
                onClick={(e) => e.stopPropagation()}
              >
                {children}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Glassmorphism info tooltip on hover — emerald tint + slide-up */}
      <AnimatePresence>
        {showInfo && info && !open && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.3, exit: { duration: 0.2 } }}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: dark
                ? 'linear-gradient(135deg, rgba(27,94,64,0.12) 0%, rgba(26,26,24,0.92) 100%)'
                : 'linear-gradient(135deg, rgba(27,94,64,0.04) 0%, rgba(250,247,242,0.95) 100%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '0 0 20px 20px',
              padding: '14px 22px',
              borderTop: '1px solid rgba(27,94,64,0.15)',
              zIndex: 5,
            }}
          >
            <Typography sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.82rem',
              color: dark ? 'rgba(250,247,242,0.7)' : colors.textMed,
              lineHeight: 1.6, textAlign: 'justify',
            }}>
              {info}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
}

export default CollapsibleSection;
